export const Mood = {
  HAPPY: 'Happy',
  EXCITED: 'Excited',
  SAD: 'Sad',
  ANGRY: 'Angry',
  CALM: 'Calm',
  NERVOUS: 'Nervous',
  CONFIDENT: 'Confident',
  SURPRISED: 'Surprised',
  SCARED: 'Scared',
} as const;

export type Mood = (typeof Mood)[keyof typeof Mood];

export const SecondaryMood = {
  CALM: {
    SERIOUS: 'Serious',
    BORED: 'Bored',
    QUIET: 'Quiet',
    THANKFUL: 'Thankful',
    PEACEFUL: 'Peaceful',
    CONTENT: 'Content',
    RELAXED: 'Relaxed',
  },
  HAPPY: {
    LOVED: 'Loved',
    EXCITED: 'Excited2',
    JOYFUL: 'Joyful',
    OPTIMISTIC: 'Optimistic',
    GRATEFUL: 'Grateful',
    AMUSED: 'Amused',
    PASSIONATE: 'Passionate',
  },
  CONFIDENT: {
    BRAVE: 'Brave',
    ASSERTIVE: 'Assertive',
    POWERFUL: 'Powerful',
    RESPECTED: 'Respected',
    INTELLIGENT: 'Intelligent',
    PROUD: 'Proud',
    IMPORTANT: 'Important',
  },
  SURPRISED: {
    STARTLED: 'Startled',
    OVERWHELMED: 'Overwhelmed',
    SHOCKED: 'Shocked',
    CONFUSED: 'Confused',
    STUNNED: 'Stunned',
    AMAZED: 'Amazed',
    FASCINATED: 'Fascinated',
  },
  ANGRY: {
    HURT: 'Hurt',
    UPSET: 'Upset',
    JEALOUS: 'Jealous',
    FRUSTRATED: 'Frustrated',
    ANNNOYED: 'Annoyed',
    OFFENDED: 'Offended',
    IRRITATED: 'Irritated',
  },
  SCARED: {
    ANXIOUS: 'Anxious',
    INTIMIDATED: 'Intimidated',
    EMBARRASSED: 'Embarrassed',
    WEAK: 'Weak',
    DISTRACTED: 'Distracted',
    STRESSED: 'Stressed',
    NERVOUS: 'Nervous',
  },
  SAD: {
    DISAPPOINTED: 'Disappointed',
    INSECURE: 'Insecure',
    GUILTY: 'Guilty',
    DEPRESSED: 'Depressed',
    LONELY: 'Lonely',
    DISCOURAGED: 'Discouraged',
    MISUNDERSTOOD: 'Misunderstood',
  },
} as const;

export const MoodReason = {
  FAMILY: 'Family',
  FRIENDS: 'Friends',
  TEACHER: 'Teacher',
  CLASSMATES: 'Classmates',
  HOMEWORK_EXAM: 'Homework/Exam',
  WORK: 'Work',
  HEALTH: 'Health',
  NOT_SURE: 'Not Sure',
} as const;

export type MoodReason = (typeof MoodReason)[keyof typeof MoodReason];

type MoodKeys = keyof typeof SecondaryMood;

type ExtractSecondaryMoodStrings<T extends MoodKeys> =
  (typeof SecondaryMood)[T][keyof (typeof SecondaryMood)[T]];

type UnionOfMoodStrings<U extends MoodKeys> = {
  [K in U]: ExtractSecondaryMoodStrings<K>;
}[U];

export type SecondaryMood = UnionOfMoodStrings<MoodKeys>;

export function getMoodDisplayName(mood: Mood | SecondaryMood): string {
  if (Object.values(Mood).includes(mood as Mood)) {
    return mood;
  }

  // combine all the strings from SecondaryMood into a single array
  const secondaryMoodStrings = Object.values(SecondaryMood).reduce(
    (acc, curr) => acc.concat(Object.values(curr)),
    [] as string[],
  );

  if (secondaryMoodStrings.includes(mood as SecondaryMood)) {
    switch (mood) {
      case SecondaryMood.HAPPY.EXCITED:
        return 'Excited/Energetic';

      case SecondaryMood.CONFIDENT.BRAVE:
        return 'Brave/Courageous';

      case SecondaryMood.SCARED.DISTRACTED:
        return 'Distracted/Forgetful';

      case SecondaryMood.SAD.GUILTY:
        return 'Guilty/Ashamed';

      case SecondaryMood.SAD.LONELY:
        return 'Lonely/Disconnected';

      default:
        return mood;
    }
  }

  throw new Error(`Invalid mood: ${mood}`);
}
